import { useState } from "react";
import { influencerStats, dropshipProducts } from "@/mocks/dropship";

interface User { name: string; email: string; role: string; }
interface Props { user: User; onLogout: () => void; }

const platformColors: Record<string, string> = {
  YouTube: "bg-red-100 text-red-600",
  TikTok: "bg-gray-900 text-white",
  Facebook: "bg-sky-100 text-sky-600",
  "Twitter/X": "bg-gray-100 text-gray-800",
  Instagram: "bg-pink-100 text-pink-600",
};

const platformIcons: Record<string, string> = {
  YouTube: "ri-youtube-line",
  TikTok: "ri-tiktok-line",
  Facebook: "ri-facebook-circle-line",
  "Twitter/X": "ri-twitter-x-line",
  Instagram: "ri-instagram-line",
};

export default function InfluencerDashboard({ user, onLogout }: Props) {
  const [tab, setTab] = useState<"leaderboard" | "products" | "myearnings">("leaderboard");
  const [selectedPlatform, setSelectedPlatform] = useState("All");

  const platforms = ["All", "YouTube", "TikTok", "Facebook", "Twitter/X"];

  const filtered = selectedPlatform === "All"
    ? influencerStats
    : influencerStats.filter((s) => s.platform === selectedPlatform || s.platform === selectedPlatform.replace("/X", ""));

  const myStats = influencerStats[1]; // Mia Chen as demo logged-in influencer

  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-400 to-pink-500 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-star-line text-white text-xl"></i>
          </div>
          <div>
            <p className="text-white/80 text-xs">Influencer / Star Account</p>
            <h3 className="text-white font-bold">{user.name}</h3>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-logout-box-line"></i> Sign Out
        </button>
      </div>

      {/* My quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b border-gray-100 bg-rose-50/40">
        {[
          { label: "Products Sold", value: myStats.productsSold.toLocaleString(), icon: "ri-shopping-bag-3-line", color: "bg-rose-100 text-rose-500" },
          { label: "Total Revenue", value: "$" + (myStats.totalRevenue / 1000).toFixed(1) + "K", icon: "ri-money-dollar-circle-line", color: "bg-amber-100 text-amber-500" },
          { label: "Commission Earned", value: "$" + myStats.commissionEarned.toLocaleString(), icon: "ri-percent-line", color: "bg-green-100 text-green-500" },
          { label: "Commission Rate", value: myStats.commissionRate + "%", icon: "ri-award-line", color: "bg-pink-100 text-pink-500" },
        ].map((s) => (
          <div key={s.label} className="text-center">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-2 ${s.color}`}>
              <i className={`${s.icon} text-lg`}></i>
            </div>
            <div className="text-xl font-extrabold text-gray-900">{s.value}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 px-5 overflow-x-auto">
        {([
          ["leaderboard", "ri-trophy-line", "Star Leaderboard"],
          ["products", "ri-store-2-line", "Promote Products"],
          ["myearnings", "ri-money-dollar-circle-line", "My Earnings"],
        ] as const).map(([t, icon, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
              tab === t ? "border-rose-400 text-rose-500" : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            <i className={icon}></i> {label}
          </button>
        ))}
      </div>

      <div className="p-5">
        {/* ── Leaderboard ── */}
        {tab === "leaderboard" && (
          <div>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <h3 className="font-bold text-gray-800">Social Stars Commission Leaderboard</h3>
              <div className="flex gap-2 flex-wrap">
                {platforms.map((p) => (
                  <button
                    key={p}
                    onClick={() => setSelectedPlatform(p)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      selectedPlatform === p
                        ? "bg-rose-400 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-rose-50 hover:text-rose-500"
                    }`}
                  >
                    {p !== "All" && <i className={`${platformIcons[p] || "ri-star-line"} mr-1`}></i>}
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {filtered.map((star, idx) => (
                <div key={star.id} className="bg-gray-50 rounded-2xl p-5">
                  <div className="flex items-start gap-4">
                    {/* Rank */}
                    <div className={`w-9 h-9 flex items-center justify-center rounded-full font-extrabold text-sm shrink-0 ${
                      idx === 0 ? "bg-amber-400 text-white" : idx === 1 ? "bg-gray-300 text-gray-700" : idx === 2 ? "bg-orange-300 text-white" : "bg-gray-100 text-gray-500"
                    }`}>
                      {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}
                    </div>

                    {/* Avatar */}
                    <div className="w-14 h-14 rounded-full overflow-hidden shrink-0">
                      <img src={star.image} alt={star.name} className="w-full h-full object-cover object-top" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-bold text-gray-900 text-sm">{star.name}</h4>
                        {star.verified && (
                          <span className="w-4 h-4 flex items-center justify-center bg-green-100 rounded-full">
                            <i className="ri-shield-check-fill text-green-500 text-xs"></i>
                          </span>
                        )}
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${platformColors[star.platform] || "bg-gray-100 text-gray-600"}`}>
                          <i className={`${star.platform_icon} mr-1`}></i>{star.platform}
                        </span>
                        <span className="text-xs text-gray-400">{star.flag} {star.country}</span>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">{star.followers} followers · Top: {star.topProduct}</p>

                      {/* Stats row */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-xl p-3 text-center">
                          <div className="text-base font-extrabold text-gray-900">{star.productsSold.toLocaleString()}</div>
                          <div className="text-xs text-gray-400">Products Sold</div>
                        </div>
                        <div className="bg-white rounded-xl p-3 text-center">
                          <div className="text-base font-extrabold text-amber-500">${(star.totalRevenue / 1000).toFixed(1)}K</div>
                          <div className="text-xs text-gray-400">Revenue Generated</div>
                        </div>
                        <div className="bg-white rounded-xl p-3 text-center">
                          <div className="text-base font-extrabold text-green-600">${star.commissionEarned.toLocaleString()}</div>
                          <div className="text-xs text-gray-400">Commission Earned</div>
                        </div>
                      </div>

                      {/* Recent sales */}
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-500 mb-2">Recent Sales</p>
                        <div className="flex flex-col gap-1.5">
                          {star.recentSales.map((sale, si) => (
                            <div key={si} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2">
                              <span className="text-gray-700 truncate flex-1">{sale.product}</span>
                              <span className="text-gray-400 mx-2 shrink-0">×{sale.qty}</span>
                              <span className="text-gray-400 mx-2 shrink-0">{sale.date}</span>
                              <span className="text-green-600 font-bold shrink-0">+${sale.commission.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Promote Products ── */}
        {tab === "products" && (
          <div>
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 mb-5 flex items-start gap-3">
              <i className="ri-information-line text-rose-400 text-lg shrink-0 mt-0.5"></i>
              <p className="text-sm text-rose-700">
                Share your unique referral link for each product. You earn commission on every sale made through your link. Commission rates are set by each seller.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dropshipProducts.map((p) => (
                <div key={p.id} className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="w-full h-36 overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-3">
                    <span className="text-xs bg-amber-50 text-amber-600 font-semibold px-2 py-0.5 rounded-full">{p.category}</span>
                    <h4 className="font-semibold text-gray-800 text-sm mt-2 mb-1 line-clamp-2">{p.name}</h4>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-amber-500 font-extrabold">${p.price}</span>
                      <span className="text-green-600 font-bold text-sm">{p.commission}% commission</span>
                    </div>
                    <div className="bg-green-50 rounded-lg px-3 py-2 mb-3 text-center">
                      <span className="text-xs text-gray-500">You earn per sale: </span>
                      <span className="text-green-600 font-extrabold text-sm">${(p.price * p.commission / 100).toFixed(2)}</span>
                    </div>
                    <button className="w-full bg-rose-400 hover:bg-rose-500 text-white text-xs font-bold py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-share-line mr-1"></i> Get Referral Link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── My Earnings ── */}
        {tab === "myearnings" && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-2xl p-5 text-center">
                <i className="ri-money-dollar-circle-line text-green-500 text-3xl mb-2 block"></i>
                <div className="text-3xl font-extrabold text-gray-900">${myStats.commissionEarned.toLocaleString()}</div>
                <div className="text-sm text-gray-500 mt-1">Total Commission Earned</div>
              </div>
              <div className="bg-amber-50 rounded-2xl p-5 text-center">
                <i className="ri-shopping-bag-3-line text-amber-500 text-3xl mb-2 block"></i>
                <div className="text-3xl font-extrabold text-gray-900">{myStats.productsSold.toLocaleString()}</div>
                <div className="text-sm text-gray-500 mt-1">Total Products Sold</div>
              </div>
              <div className="bg-rose-50 rounded-2xl p-5 text-center">
                <i className="ri-percent-line text-rose-500 text-3xl mb-2 block"></i>
                <div className="text-3xl font-extrabold text-gray-900">{myStats.commissionRate}%</div>
                <div className="text-sm text-gray-500 mt-1">Average Commission Rate</div>
              </div>
            </div>

            <h3 className="font-bold text-gray-800 mb-4">Recent Commission Transactions</h3>
            <div className="flex flex-col gap-3">
              {myStats.recentSales.map((sale, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{sale.product}</p>
                    <p className="text-xs text-gray-400">Qty: {sale.qty} · {sale.date}</p>
                  </div>
                  <div className="text-right ml-3 shrink-0">
                    <div className="font-extrabold text-green-600">+${sale.commission.toFixed(2)}</div>
                    <div className="text-xs text-gray-400">Commission</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-amber-50 rounded-xl p-5 flex items-center justify-between">
              <div>
                <p className="font-bold text-gray-800">Ready to withdraw?</p>
                <p className="text-xs text-gray-500 mt-0.5">Minimum withdrawal: $50. Paid via PayPal, bank transfer or WeChat Pay.</p>
              </div>
              <button className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">
                Withdraw Earnings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
